import {
  LitElement, html, nothing,
  type TemplateResult, type CSSResultGroup, type PropertyValues,
} from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { emit } from '../utils/dom.utils.js';
import styles from './styles.js';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CarouselResponsiveOption {
  /** Max viewport width (px) this breakpoint applies up to. */
  breakpoint: string;
  numVisible: number;
  numScroll:  number;
}

export type CarouselOrientation = 'horizontal' | 'vertical';

/**
 * uwc-carousel
 *
 * Feature-rich carousel / slider component.
 *
 * @attr {unknown[]}                  items             - Data array to render. Set via JS property.
 * @attr {number}                     num-visible       - Items visible at once. Default: 3.
 * @attr {number}                     num-scroll        - Items scrolled per step. Default: 1.
 * @attr {boolean}                    circular          - Wrap-around navigation.
 * @attr {number}                     autoplay-interval - Autoplay delay in ms (0 = off).
 * @attr {CarouselOrientation}        orientation       - horizontal | vertical. Default: horizontal.
 * @attr {boolean}                    show-navigators   - Show prev/next buttons. Add attribute to enable; omit to hide. Default: false.
 * @attr {boolean}                    show-indicators   - Show dot indicators. Default: true.
 * @attr {string}                     prev-icon         - Bootstrap Icon name for prev. Default: chevron-left.
 * @attr {string}                     next-icon         - Bootstrap Icon name for next. Default: chevron-right.
 * @attr {string}                     aria-label        - Accessible label for the carousel region.
 *
 * @prop {(item, index) => TemplateResult} itemTemplate - Custom render function for each item.
 * @prop {CarouselResponsiveOption[]}      responsiveOptions - Breakpoint overrides.
 *
 * @slot header  - Content rendered above the carousel.
 * @slot footer  - Content rendered below the carousel.
 *
 * @fires uwc-page      - Fires when the page changes. detail: { page: number }
 * @fires uwc-autoplay  - Fires on each autoplay step. detail: { page: number }
 *
 * @cssprop --uwc-carousel-duration       - Slide animation duration. Default: 400ms.
 * @cssprop --uwc-carousel-dot-color      - Inactive dot colour.
 * @cssprop --uwc-carousel-dot-hover      - Dot hover colour.
 * @cssprop --uwc-carousel-dot-active     - Active dot colour.
 */
export class UwcCarousel extends LitElement {

  static override styles: CSSResultGroup = styles;

  // ── Properties ─────────────────────────────────────────────────────────────

  @property({ type: Array })
  items: unknown[] = [];

  @property({ type: Number, attribute: 'num-visible' })
  numVisible = 3;

  @property({ type: Number, attribute: 'num-scroll' })
  numScroll = 1;

  @property({ type: Boolean, reflect: true })
  circular = false;

  @property({ type: Number, attribute: 'autoplay-interval' })
  autoplayInterval = 0;

  @property({ reflect: true })
  orientation: CarouselOrientation = 'horizontal';

  @property({ type: Boolean, attribute: 'show-navigators' })
  showNavigators = false;

  @property({ type: Boolean, attribute: 'show-indicators' })
  showIndicators = true;

  @property({ attribute: 'prev-icon' })
  prevIcon = 'chevron-left';

  @property({ attribute: 'next-icon' })
  nextIcon = 'chevron-right';

  @property({ attribute: 'aria-label' })
  override ariaLabel: string | null = 'Carousel';

  /** Custom item render function. `(item: unknown, index: number) => TemplateResult` */
  itemTemplate?: (item: unknown, index: number) => TemplateResult;

  /** Breakpoint responsive overrides. */
  responsiveOptions: CarouselResponsiveOption[] = [];

  // ── Internal state ──────────────────────────────────────────────────────────

  @state() private _page = 0;
  @state() private _effectiveNumVisible = 3;
  @state() private _effectiveNumScroll  = 1;
  @state() private _animating = false;
  /** Measured height of the viewport element — used for pixel-accurate vertical scrolling. */
  @state() private _viewportH = 0;

  @query('.uwc-car__track') private _track?: HTMLElement;
  @query('.uwc-car__viewport') private _viewport?: HTMLElement;

  private _autoplayTimer?: ReturnType<typeof setInterval>;
  private _resizeObserver?: ResizeObserver;
  private _pointerStartX = 0;
  private _pointerStartY = 0;
  private _pointerDeltaX = 0;
  private _pointerDeltaY = 0;
  private _dragging = false;
  private _liveLabel = '';

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  override connectedCallback() {
    super.connectedCallback();
    this._resizeObserver = new ResizeObserver(() => {
      this._applyResponsive();
      // Re-measure viewport height whenever the host is resized (covers vertical mode).
      requestAnimationFrame(() => this._measureViewport());
    });
    this._resizeObserver.observe(this);
    this._applyResponsive();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._resizeObserver?.disconnect();
    this._stopAutoplay();
  }

  override firstUpdated() {
    this._measureViewport();
  }

  override updated(changed: PropertyValues) {
    if (changed.has('autoplayInterval')) {
      this._stopAutoplay();
      this._startAutoplay();
    }
    if (changed.has('items') || changed.has('numVisible') || changed.has('numScroll')) {
      this._applyResponsive();
      // Clamp page if items shrank
      const maxPage = this._maxPage;
      if (this._page > maxPage) this._page = maxPage;
    }
    if (changed.has('orientation')) {
      // Re-measure when orientation changes to/from vertical
      requestAnimationFrame(() => this._measureViewport());
    }
  }

  // ── Responsive ─────────────────────────────────────────────────────────────

  private _measureViewport() {
    const h = this._viewport?.offsetHeight ?? 0;
    if (h > 0 && h !== this._viewportH) this._viewportH = h;
  }

  private _applyResponsive() {
    const w = this.offsetWidth;
    let nv = this.numVisible;
    let ns = this.numScroll;

    if (this.responsiveOptions.length) {
      // Sort descending so first match (smallest breakpoint >= width) wins
      const sorted = [...this.responsiveOptions].sort(
        (a, b) => parseInt(b.breakpoint) - parseInt(a.breakpoint)
      );
      for (const opt of sorted) {
        if (w <= parseInt(opt.breakpoint)) {
          nv = opt.numVisible;
          ns = opt.numScroll;
        }
      }
    }

    this._effectiveNumVisible = Math.max(1, Math.min(nv, this.items.length || 1));
    this._effectiveNumScroll  = Math.max(1, ns);
    if (this.orientation === 'vertical') {
      // Re-measure after layout settles — nv/ns change may affect viewport height
      requestAnimationFrame(() => this._measureViewport());
    }
  }

  // ── Pagination ──────────────────────────────────────────────────────────────

  private get _totalPages(): number {
    const n = this.items.length;
    if (n <= this._effectiveNumVisible) return 1;
    return Math.ceil((n - this._effectiveNumVisible) / this._effectiveNumScroll) + 1;
  }

  private get _maxPage(): number {
    return Math.max(0, this._totalPages - 1);
  }

  private get _isPrevDisabled(): boolean {
    return !this.circular && this._page === 0;
  }

  private get _isNextDisabled(): boolean {
    return !this.circular && this._page >= this._maxPage;
  }

  private _goTo(page: number, byAutoplay = false) {
    const total = this._totalPages;
    let next = page;
    if (this.circular) {
      next = ((page % total) + total) % total;
    } else {
      next = Math.max(0, Math.min(page, this._maxPage));
    }
    if (next === this._page) return;

    this._animating = true;
    this._page = next;

    const startItem = next * this._effectiveNumScroll;
    const endItem   = Math.min(startItem + this._effectiveNumVisible, this.items.length);
    this._liveLabel = `Slide ${startItem + 1} to ${endItem} of ${this.items.length}`;

    emit(this, byAutoplay ? 'uwc-autoplay' : 'uwc-page', { page: next });

    // Remove animation class after transition ends
    const track = this._track;
    if (track) {
      const done = () => {
        this._animating = false;
        track.removeEventListener('transitionend', done);
      };
      track.addEventListener('transitionend', done, { once: true });
      // Fallback in case transitionend doesn't fire (reduced-motion / display:none)
      setTimeout(() => { this._animating = false; }, 500);
    }
  }

  private _prev() { this._goTo(this._page - 1); }
  private _next() { this._goTo(this._page + 1); }

  // ── Public navigation API ────────────────────────────────────────────────────

  /** Navigate to the previous page. No-op when already at the first page (non-circular). */
  prev() { this._prev(); }

  /** Navigate to the next page. No-op when already at the last page (non-circular). */
  next() { this._next(); }

  /** Navigate to an explicit page index (0-based). */
  goTo(page: number) { this._goTo(page); }

  /** Total number of pages for the current items / numVisible / numScroll configuration. */
  get totalPages() { return this._totalPages; }

  /** Whether the previous button would be disabled (first page, non-circular). */
  get isPrevDisabled() { return this._isPrevDisabled; }

  /** Whether the next button would be disabled (last page, non-circular). */
  get isNextDisabled() { return this._isNextDisabled; }

  // ── Autoplay ─────────────────────────────────────────────────────────────────

  private _startAutoplay() {
    if (!this.autoplayInterval || this.autoplayInterval <= 0) return;
    this._autoplayTimer = setInterval(() => {
      const next = this.circular
        ? (this._page + 1) % this._totalPages
        : this._page < this._maxPage ? this._page + 1 : 0;
      this._goTo(next, true);
    }, this.autoplayInterval);
  }

  private _stopAutoplay() {
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = undefined;
    }
  }

  // ── Touch / pointer swipe ───────────────────────────────────────────────────

  private _onPointerDown(e: PointerEvent) {
    if (e.button !== 0) return;
    this._pointerStartX = e.clientX;
    this._pointerStartY = e.clientY;
    this._pointerDeltaX = 0;
    this._pointerDeltaY = 0;
    this._dragging = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this._stopAutoplay();
  }

  private _onPointerMove(e: PointerEvent) {
    if (!this._dragging) return;
    this._pointerDeltaX = e.clientX - this._pointerStartX;
    this._pointerDeltaY = e.clientY - this._pointerStartY;
  }

  private _onPointerUp() {
    if (!this._dragging) return;
    this._dragging = false;
    const threshold = 50;
    const isHoriz = this.orientation === 'horizontal';
    const delta = isHoriz ? this._pointerDeltaX : this._pointerDeltaY;

    if (Math.abs(delta) >= threshold) {
      delta < 0 ? this._next() : this._prev();
    }
    this._startAutoplay();
  }

  // ── Keyboard navigation ──────────────────────────────────────────────────────

  private _onKeyDown(e: KeyboardEvent) {
    const isHoriz = this.orientation === 'horizontal';
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        if (isHoriz ? e.key === 'ArrowLeft' : e.key === 'ArrowUp') {
          e.preventDefault();
          this._prev();
        }
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        if (isHoriz ? e.key === 'ArrowRight' : e.key === 'ArrowDown') {
          e.preventDefault();
          this._next();
        }
        break;
      case 'Home':
        e.preventDefault();
        this._goTo(0);
        break;
      case 'End':
        e.preventDefault();
        this._goTo(this._maxPage);
        break;
    }
  }

  // ── Default item template ────────────────────────────────────────────────────

  private _renderItem(item: unknown, index: number): TemplateResult {
    if (this.itemTemplate) return this.itemTemplate(item, index);

    // Sensible default for plain-object items
    const it = item as Record<string, unknown>;
    return html`
      <div class="uwc-car__default-item">
        ${it['image'] ? html`<img src=${String(it['image'])} alt=${String(it['alt'] ?? it['title'] ?? '')} loading="lazy" decoding="async" />` : nothing}
        ${it['title'] ? html`<p class="uwc-car__default-title">${it['title']}</p>` : nothing}
        ${it['subtitle'] ? html`<p class="uwc-car__default-subtitle">${it['subtitle']}</p>` : nothing}
        ${it['content'] ? html`<p class="uwc-car__default-content">${it['content']}</p>` : nothing}
      </div>
    `;
  }

  // ── Computed transform ───────────────────────────────────────────────────────

  private get _trackStyle() {
    if (this.orientation === 'vertical') {
      // Use pixel-based offset so translateY(%) doesn't overshoot
      // (translateY(%) is relative to the *track* height, not the viewport).
      if (this._viewportH > 0) {
        const itemH    = this._viewportH / this._effectiveNumVisible;
        const offsetPx = -(this._page * this._effectiveNumScroll * itemH);
        return { transform: `translateY(${offsetPx}px)` };
      }
      // Fallback before viewport is measured — use percentage (may overshoot, but
      // _measureViewport() will fire via firstUpdated / rAF and correct it).
      const itemPct   = 100 / this._effectiveNumVisible;
      const offsetPct = -(this._page * this._effectiveNumScroll * itemPct);
      return { transform: `translateY(${offsetPct}%)` };
    }
    const itemPct   = 100 / this._effectiveNumVisible;
    const offsetPct = -(this._page * this._effectiveNumScroll * itemPct);
    return { transform: `translateX(${offsetPct}%)` };
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  override render() {
    const itemPct   = 100 / this._effectiveNumVisible;
    const isHoriz   = this.orientation === 'horizontal';
    const pages     = this._totalPages;

    // For vertical mode use pixel heights (derived from measured viewport) so
    // items fill exactly 1/numVisible of the viewport — percentage heights on
    // flex children require a definite parent height, which varies by usage.
    const itemH = (!isHoriz && this._viewportH > 0)
      ? this._viewportH / this._effectiveNumVisible
      : null;

    const itemStyle = isHoriz
      ? { width: `${itemPct}%` }
      : { height: itemH != null ? `${itemH}px` : `${itemPct}%`, width: '100%' };

    return html`
      <!-- Live region for screen readers -->
      <div
        aria-live="polite"
        aria-atomic="true"
        class="visually-hidden"
        style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap"
      >${this._liveLabel}</div>

      <!-- Header slot -->
      <div class="uwc-car__header"><slot name="header"></slot></div>

      <!-- Carousel region -->
      <div
        class="uwc-car__region"
        role="region"
        aria-label=${this.ariaLabel ?? 'Carousel'}
        aria-roledescription="carousel"
      >
        <!-- Viewport -->
        <div
          class="uwc-car__viewport"
          @keydown=${this._onKeyDown}
          tabindex="0"
          aria-label="Slide container"
          @pointerdown=${this._onPointerDown}
          @pointermove=${this._onPointerMove}
          @pointerup=${this._onPointerUp}
          @pointercancel=${this._onPointerUp}
        >
          <div
            class=${classMap({ 'uwc-car__track': true, 'is-animating': this._animating })}
            style=${styleMap(this._trackStyle)}
            aria-live="off"
          >
            ${repeat(
              this.items,
              (_, i) => i,
              (item, index) => html`
                <div
                  class="uwc-car__item"
                  style=${styleMap(itemStyle)}
                  role="group"
                  aria-roledescription="slide"
                  aria-label="Slide ${index + 1} of ${this.items.length}"
                >
                  ${this._renderItem(item, index)}
                </div>
              `
            )}
          </div>
        </div>

        <!-- Navigation buttons -->
        ${this.showNavigators ? html`
          <div class="uwc-car__nav" aria-hidden="true">
            <button
              class="uwc-car__prev"
              type="button"
              aria-label="Previous slide"
              ?disabled=${this._isPrevDisabled}
              @click=${this._prev}
            >
              <uwc-icon name=${this.prevIcon} size="1.15em"></uwc-icon>
            </button>
            <button
              class="uwc-car__next"
              type="button"
              aria-label="Next slide"
              ?disabled=${this._isNextDisabled}
              @click=${this._next}
            >
              <uwc-icon name=${this.nextIcon} size="1.15em"></uwc-icon>
            </button>
          </div>
        ` : nothing}
      </div>

      <!-- Indicators -->
      ${this.showIndicators && pages > 1 ? html`
        <div class="uwc-car__indicators" role="tablist" aria-label="Slide indicators">
          ${Array.from({ length: pages }, (_, i) => html`
            <button
              class=${classMap({ 'uwc-car__dot': true, active: i === this._page })}
              type="button"
              role="tab"
              aria-selected=${i === this._page ? 'true' : 'false'}
              aria-label="Go to slide ${i + 1}"
              @click=${() => this._goTo(i)}
            ></button>
          `)}
        </div>
      ` : nothing}

      <!-- Footer slot -->
      <div class="uwc-car__footer"><slot name="footer"></slot></div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-carousel': UwcCarousel; }
}
